'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '~/src/lib/utils'

interface SeoReportTruncatedCopyProps {
  children: ReactNode
  maxHeight: number
  className?: string
}

export function SeoReportTruncatedCopy({
  children,
  maxHeight,
  className,
}: SeoReportTruncatedCopyProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const check = () => setIsOverflowing(el.scrollHeight > maxHeight)
    check()

    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [maxHeight])

  const isTruncated = isOverflowing && !isExpanded

  return (
    <div className={cn(className)}>
      <div className='relative'>
        <div
          ref={contentRef}
          style={!isExpanded ? { maxHeight, overflow: 'hidden' } : undefined}
        >
          {children}
        </div>
        {isTruncated ? (
          <div className='pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent' />
        ) : null}
      </div>
      {isTruncated ? (
        <button
          type='button'
          onClick={() => setIsExpanded(true)}
          className='mt-2 text-[14px] font-medium text-foreground-heading hover:underline'
        >
          Read more
        </button>
      ) : null}
    </div>
  )
}
